import { forwardRef, type ReactNode, isValidElement } from 'react'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { CardOpenControl } from '@/components/ui/card-open-control'
import { cn } from '@/lib/utils'

export type ListCardProps = {
  /** Primary header row(s); omitted when empty. */
  header?: ReactNode
  /** Main content (description, meta rows, tags). Omitted when empty. */
  body?: ReactNode
  /** Footnote, actions, timestamps. Omitted when empty. Rendered in `CardFooter`. */
  footer?: ReactNode
  bodyClassName?: string
  footerClassName?: string
  /**
   * When set, the card activates via click, Enter, and Space.
   * Keep navigation and data loading in the parent.
   */
  onOpen?: () => void
  /** Prefer setting when `onOpen` is used (screen readers). */
  openAriaLabel?: string
  /**
   * Row inside a parent card (e.g. skeleton rows, picker panels). Omits outer `Card` chrome;
   * use dividers on the parent between siblings.
   */
  embedded?: boolean
  /** Key for `data-agent-highlight` (scroll + flash via `?agentHighlight=`). */
  agentHighlight?: string
  className?: string
  /** Spread onto the outer interactive surface (e.g. dnd-kit drag listeners on pipeline cards). */
  surfaceProps?: Record<string, unknown>
}

function isPresent(node: ReactNode): boolean {
  if (node == null || node === false) return false
  if (typeof node === 'string') return node.trim().length > 0
  if (Array.isArray(node)) return node.some(isPresent)
  if (isValidElement(node)) return true
  return true
}

/**
 * Shared list-style card: optional header, body, and footer sections.
 * Empty sections are not rendered (no reserved height).
 * Entity-specific UIs (e.g. `job-card`, `organisation-card`) compose this shell.
 *
 * Interactivity: the accessible control is a stretched, pointer-transparent
 * button element ({@link CardOpenControl}) while mouse clicks resolve on the
 * container — inner actions stay natively interactive, never nested in a button role.
 */
export const ListCard = forwardRef<HTMLDivElement, ListCardProps>(function ListCard(
  {
    header,
    body,
    footer,
    bodyClassName,
    footerClassName,
    onOpen,
    openAriaLabel,
    embedded = false,
    agentHighlight,
    className,
    surfaceProps,
  },
  ref,
) {
  const interactive = typeof onOpen === 'function'

  const bodyClass = cn('pointer-events-none', bodyClassName)
  const footerClass = cn('flex-col items-stretch px-0 [.border-t]:pt-[var(--sp-3)]', footerClassName)

  const openControl = interactive ? (
    <CardOpenControl
      ariaLabel={openAriaLabel ?? 'Open'}
      onOpen={() => onOpen?.()}
      rounded={embedded ? 'md' : 'xl'}
    />
  ) : null

  const inner = (
    <>
      {openControl}
      {isPresent(header) ? (
        embedded ? (
          // `@container/card-header` mirrors CardHeader so `@md:` header rows
          // measure the card, not the distant page pane.
          <div className="@container/card-header grid auto-rows-min gap-1.5">{header}</div>
        ) : (
          <CardHeader>{header}</CardHeader>
        )
      ) : null}
      {isPresent(body) ? (
        embedded ? (
          <div className={bodyClass}>{body}</div>
        ) : (
          <CardContent className={bodyClass}>{body}</CardContent>
        )
      ) : null}
      {isPresent(footer) ? (
        embedded ? (
          <div className={footerClass}>{footer}</div>
        ) : (
          <CardFooter className={footerClass}>{footer}</CardFooter>
        )
      ) : null}
    </>
  )

  if (embedded) {
    return (
      <div
        {...(agentHighlight ? { 'data-agent-highlight': agentHighlight } : {})}
        className={cn(
          'relative flex flex-col gap-3',
          interactive && 'cursor-pointer rounded-md transition-colors hover:bg-muted/50',
          className,
        )}
        onClick={interactive ? () => onOpen?.() : undefined}
      >
        {inner}
      </div>
    )
  }

  return (
    <Card
      ref={ref}
      {...(agentHighlight ? { 'data-agent-highlight': agentHighlight } : {})}
      className={cn(interactive && 'cursor-pointer transition-all hover:shadow-md', className)}
      onClick={interactive ? () => onOpen?.() : undefined}
      {...surfaceProps}
    >
      {inner}
    </Card>
  )
})

ListCard.displayName = 'ListCard'
